// BottomTabBar.jsx
// Floating glass tab bar — the phone-tier primary navigation. The mobile app is
// a MONITORING cockpit (you build algos on desktop; on mobile you watch them
// run), so the destinations are monitoring lenses at increasing depth:
//   Watch · Algos · [Home] · Orders · More
// Home (the overall dashboard) owns the weighted CENTER slot; Algos + Orders —
// the app's unique value — flank it with the best thumb reach.
//
// Icons MATCH the web sidebar (src/data/staticdata.js) so mobile ↔ web
// correlate at a glance: Home=FiBarChart, Watch=FiStar, Orders=FiShoppingCart,
// Algos=MdPrecisionManufacturing. If an icon changes, change it in BOTH places.
//
// Theme-aware via DS CSS vars. Sits above the home-indicator via safe-area inset.
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FiBarChart, FiStar, FiShoppingCart, FiMoreHorizontal } from "react-icons/fi";
import { MdPrecisionManufacturing } from "react-icons/md";
import { TabBar } from "../ds";

// Primary destinations → real app routes. `match` decides the active tab from
// the current path (longest prefix wins, handled in activeKey). `center` marks
// the weighted hero slot. The last ("More") opens a sheet instead of navigating.
export const TABS = [
  { key: "watch", label: "Watch", route: "/WatchList", match: ["/WatchList"], icon: <FiStar /> },
  { key: "algos", label: "Algos", route: "/AlgoDashboard", match: ["/AlgoDashboard"], icon: <MdPrecisionManufacturing /> },
  { key: "home", label: "Home", route: "/Overview", match: ["/Overview", "/Portfolio"], icon: <FiBarChart />, center: true },
  { key: "orders", label: "Orders", route: "/Orders", match: ["/Orders"], icon: <FiShoppingCart /> },
  { key: "more", label: "More", isMore: true, icon: <FiMoreHorizontal /> },
];

function activeKey(pathname, moreOpen) {
  if (moreOpen) return "more";
  // Longest prefix wins so /AlgoDashboard/:id still lights "Algos".
  let best = null;
  let bestLen = -1;
  for (const t of TABS) {
    for (const p of t.match || []) {
      if (pathname.toLowerCase().startsWith(p.toLowerCase()) && p.length > bestLen) {
        best = t.key;
        bestLen = p.length;
      }
    }
  }
  return best;
}

export default function BottomTabBar({ moreOpen = false, onMore }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const current = activeKey(pathname, moreOpen);

  const tabs = TABS.map((t) => ({ key: t.key, label: t.label, icon: t.icon, center: t.center }));

  const handleSelect = (key) => {
    const t = TABS.find((x) => x.key === key);
    if (t?.isMore) return onMore?.();
    if (t?.route) navigate(t.route);
  };

  return <TabBar tabs={tabs} activeKey={current} onSelect={handleSelect} />;
}
